import { formatToPercent } from "@inrev/common";
import { Modal } from "@portal/components/layout/Modal";
import { PartialIndicator } from "@portal/components/primitives";
import { Button } from "@portal/components/ui/Button";
import { Card } from "@portal/components/ui/Card";
import { useMemo } from "react";

export type AccountPartialSubmitConfirmationModalProps = {
	context: "submission" | "view";
	userContext: "admin" | "agent";
	/** Ratio (0-1) of partial fields that have a value. Omit to hide the completion indicator. */
	percentComplete?: number;
	onClose: () => void;
	onSubmit: () => void;
};

export const AccountPartialSubmitConfirmationModal = ({
	context,
	userContext,
	percentComplete,
	onClose,
	onSubmit,
}: AccountPartialSubmitConfirmationModalProps) => {
	const formattedPercentComplete = useMemo(() => {
		if (percentComplete === undefined) return undefined;
		return formatToPercent(Math.max(Math.min(percentComplete, 1), 0), 0);
	}, [percentComplete]);

	const confirmationHeader = useMemo(() => {
		if (context === "submission") {
			return "Ineligible for Auto-Approval";
		}
		return "Incomplete Data";
	}, [context]);

	const confirmationBody = useMemo(() => {
		if (context === "submission") {
			return "Some information is missing, so this submission will be referred to an underwriter for review.";
		}
		if (userContext === "agent") {
			return "Some information is missing, so this will be referred to an underwriter for further review.";
		}
		return "Some information is missing. You may update the status directly after submitting.";
	}, [context]);

	const confirmationSubtext = useMemo(() => {
		if (context === "submission") {
			return "After submitting, you can provide additional information on the Account page.";
		}
	}, [context]);
	return (
		<Modal onClickOutside={onClose}>
			<Card className="flex flex-col items-center w-[550px] max-w-[90vw] gap-[10px] overflow-hidden">
				<div className="w-full flex items-center space-x-[12px] justify-between bg-intent-warning-bg px-[18px] py-[10px]">
					<span className="text-[16px] font-[550]">{confirmationHeader}</span>
					{formattedPercentComplete && (
						<span className="text-[14px] font-semibold [word-spacing:4px]">
							{formattedPercentComplete} Complete
						</span>
					)}
				</div>
				<div className="flex flex-1 px-5 py-4 text-[16px] text-primary gap-[10px]">
					<span className="mt-2 px-3">
						<PartialIndicator active />
					</span>
					<div className="flex flex-col flex-1 gap-3 text-[18px]">
						<span>{confirmationBody}</span>
						{confirmationSubtext && (
							<span className="text-[16px] text-secondary">{confirmationSubtext}</span>
						)}
					</div>
				</div>
				<div className="w-full flex justify-center gap-[10px] px-4 py-3 mb-[10px]">
					<Button
						color="primary"
						className="order-2 px-[20px] min-w-[120px]"
						onClick={() => onSubmit()}
						filled
					>
						<span>Submit</span>
					</Button>
					<Button color="secondary" className="order-1 w-[120px]" onClick={onClose}>
						Back
					</Button>
				</div>
			</Card>
		</Modal>
	);
};
